import { segmentChanged } from './segmentChanged';
import {
  DATE_RANGE_TYPE,
  DATE_UNITS,
  convertFromNewUiToQueryFormat,
  convertFromQueryToNewUiFormat,
} from '@hotjar/global-filters';
import { RecordingPlaybackTracker } from '@hotjar/webapp/recordings/recording-playback-tracker';
import { track } from '@hotjar/webapp/domains/common/tracking/segment';
import { RELEVANCE_CATEGORY_LIST, RELEVANCE_SCORE_TO_NAME } from '../../../constants/relevance';
import { extractSiteGroupingProps } from '@hotjar/webapp/tracking/utils';
import { LocalStorage } from '@hotjar/insights-common/lib/storage-factory';
import type { Site } from '@hotjar/site';
import { countBy, uniq } from 'lodash-es';

import type { Store, JsonFilters, Recording } from '../../../store/list/types';

export type EffectiveQuery = {
  DAYS_AGO: Record<string, number>;
  AND: Record<string, Record<string, number>>[];
};

/**
 * Transform the jsonFilters into a flat list and call
 */
export function flattenFiltersAndTagRepeated(jsonFilters: JsonFilters): void {
  const filtersInUiFormat = convertFromQueryToNewUiFormat(jsonFilters);
  const flatList = filtersInUiFormat.filterList.map((item) => ({ ...item, type: item.kind }));
  RecordingPlaybackTracker.tagRepeatedFilters(flatList);
}

/**
 * Extracts and returns the effective days ago value for a given filter.
 * Supports both filter formats
 *  - `{ DAYS_AGO: { created: 30 }}`
 *  - `{ AND : [{ DAYS_AGO : { created: 30 }}, { WATCHED: true }]}`
 */
export function extractDaysAgo(effectiveQuery: EffectiveQuery): number | undefined {
  if (effectiveQuery.DAYS_AGO) {
    return effectiveQuery.DAYS_AGO.created;
  }

  let dateFilter;
  if (effectiveQuery.AND) {
    dateFilter = effectiveQuery.AND.find((x) => !!x.DAYS_AGO);
  }

  return dateFilter ? dateFilter.DAYS_AGO.created : undefined;
}

/**
 * Broadcast to child segment-bar a date range override (for "Last 6 Months" buttons).
 */
export function setCurrentSegmentDateRange(store: Store, days: string): void {
  const { jsonFilters } = store.getState();
  const newFilterUiFormat = convertFromQueryToNewUiFormat(jsonFilters as JsonFilters);
  if (+days === 0) {
    newFilterUiFormat.dateRange = {
      type: DATE_RANGE_TYPE.RELATIVE,
      unit: DATE_UNITS.HOURS,
      amount: 24,
    };
  } else if (+days === 365) {
    newFilterUiFormat.dateRange = {
      type: DATE_RANGE_TYPE.RELATIVE,
      unit: DATE_UNITS.MONTHS,
      amount: 12,
    };
  } else {
    newFilterUiFormat.dateRange = {
      type: DATE_RANGE_TYPE.RELATIVE,
      unit: DATE_UNITS.DAYS,
      amount: +days,
    };
  }
  const jsonFiltersConverted = convertFromNewUiToQueryFormat(newFilterUiFormat);
  const queryString = JSON.stringify(jsonFiltersConverted);

  const searchParams = new URLSearchParams(window.location.search);

  searchParams.delete('segment');
  searchParams.set('filters', queryString);

  window.history.replaceState(
    null,
    '',
    `${window.location.pathname}?${searchParams.toString()}`.replaceAll('%3A', ':'),
  );
  segmentChanged(store, jsonFiltersConverted as JsonFilters);
}

/**
 * Gather and send data to segment.
 */
export function trackRecordingListViewed(store: Store): void {
  const {
    site,
    hotjarFilters,
    recordings,
    recordingSettings: {
      excludeMinimalActivity,
      excludeShortRecordings,
      recordingCaptureKeystrokes,
      recordingTargeting,
    },
    totalHits,
  } = store.getState();

  const recordingSnapshotOptions = [];
  if (excludeMinimalActivity) {
    recordingSnapshotOptions.push('Only click & scroll sessions');
  }
  if (excludeShortRecordings) {
    recordingSnapshotOptions.push('Only 30s+ sessions');
  }
  if (recordingCaptureKeystrokes) {
    recordingSnapshotOptions.push('Record keystroke data');
  }

  const recordingTargetingOptions = [];
  if (recordingTargeting.urlRules.length) {
    recordingTargetingOptions.push('Visit a specific page');
  } else {
    recordingTargetingOptions.push('Visit any page');
  }
  if (recordingTargeting.triggerRules.length) {
    recordingTargetingOptions.push('JavaScript trigger fires');
  }

  const recordingURLOptions = uniq(recordingTargeting.urlRules.map((rule) => rule.match_operation));

  const scoreCounters = countRelevanceByCategory(
    recordings,
    RELEVANCE_CATEGORY_LIST,
    RELEVANCE_SCORE_TO_NAME,
  );

  let optionalFields = {};

  if (recordingURLOptions?.length > 0) {
    optionalFields = {
      recording_snapshot_targeting_url_options: recordingURLOptions,
    };
  }

  const trackPayload = {
    number_recordings_in_list: totalHits,
    recording_snapshot_options: recordingSnapshotOptions,
    recording_snapshot_targeting: recordingTargetingOptions,
    recordings_list_actions_view_filter: hotjarFilters.watched,
    relevance_score_list: scoreCounters,
    ...extractSiteGroupingProps(site as Site),
  };

  track('Recording List Viewed', { ...trackPayload, ...optionalFields });
}

/**
 * Generate an array counting the number of recordings in the list grouped by the 5 relevance categories
 * from "very low" to "very high"
 * @param recordingList the list of recordings.
 * @param relevanceCategoryList the list of relevance category.
 * @param relevanceScoreToName the mapping between category name and score.
 * @returns an array of 5 number (for example [5, 0, 1, 1, 3])
 */
export function countRelevanceByCategory(
  recordingList: Recording[],
  relevanceCategoryList: Record<string, string>,
  relevanceScoreToName: Record<number, string>,
): number[] {
  const countByCategoryName = countBy(
    recordingList,
    (r) => relevanceScoreToName[r.relevance_score],
  );
  const relevanceCategoryName = Object.values(relevanceCategoryList);
  return relevanceCategoryName.reduce((acc: number[], name, index) => {
    acc[index] = countByCategoryName[name] || 0;
    return acc;
  }, []);
}

/**
 * Return true if hotjarFiltersPreferences exist in the local storage
 */
export function isHotjarFiltersPreferencesInLocalStorage(): boolean {
  const filterPreferences = LocalStorage.get('hotjarFiltersPreferences');

  return !!filterPreferences;
}

/**
 * Return true if the user have seen the popover info for not watched by default
 */
export function isNotWatchedPopoverInfoSeen(): boolean {
  return !!LocalStorage.get('isPopoverNotWatchedSeen');
}
